// apps/admin/src/pages/whitelist/tabs/WhitelistLogTab.tsx
//
// Who opened or closed card payment for which merchant, and when.
//
// No new storage. Every `PUT /admin/merchants/:id/card-payment-permission`
// has always written an audit row (`CARD_PAYMENT_PERMISSION_UPDATED`, with a
// full before/after snapshot) — there was simply no screen that filtered
// them out of the platform-wide audit log. So this tab is a reader, and the
// history from before it existed shows up on first load.
//
// Reads the warm audit endpoint through the v2 client, which is what the
// live `/admin/audit-log` route speaks. Rows arrive as a FLAT DIFF
// (`[{path, before, after}]`) rather than before/after blobs, so the change
// type is derived from the `enabled` path rather than by comparing objects.
//
// Note these rows are written with `target_type: 'merchant'` (lowercase),
// which does not match the `Merchants` entity the shared Audit Logs filter
// offers — they are invisible there. Filtering by `action` here sidesteps
// that, and renaming the stored value would orphan every row already
// written.

import { useEffect, useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  listAuditLogWarm,
  type AuditV2Row,
  type AuditV2ApiError,
} from '../../../api/audit-log-v2.api.js';
import { Money } from '../../../components/ui/Money.js';
import { useTranslation } from '../../../i18n/useTranslation.js';

/** The one action this screen is about. */
const WHITELIST_ACTION = 'CARD_PAYMENT_PERMISSION_UPDATED';

const PAGE_SIZE = 25;

type ChangeKind = 'added' | 'removed' | 'limits';

/**
 * What a row actually did, from the flat diff.
 *
 * `enabled` moving false→true is an add and true→false a removal; a row that
 * does not touch `enabled` only moved the ceilings. Falling back to "limits"
 * matters because a diff can legitimately contain only ceiling paths, and
 * labelling that as an add would misreport who was granted access.
 */
function classify(row: AuditV2Row): ChangeKind {
  const enabled = row.diff.find((d) => d.path === 'enabled');
  if (!enabled) return 'limits';
  if (enabled.after === true) return 'added';
  if (enabled.after === false) return 'removed';
  return 'limits';
}

/** Read a ceiling's post-change value out of the diff; `undefined` = untouched. */
function limitAfter(row: AuditV2Row, path: string): number | null | undefined {
  const entry = row.diff.find((d) => d.path === path);
  if (!entry) return undefined;
  const v = entry.after;
  if (v === null) return null;
  return typeof v === 'number' ? v : undefined;
}

export function WhitelistLogTab(): JSX.Element {
  const { t } = useTranslation('finance');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);

  const query = useQuery<
    Awaited<ReturnType<typeof listAuditLogWarm>>,
    AuditV2ApiError
  >({
    queryKey: ['admin', 'whitelist', 'log', from, to, page],
    queryFn: () =>
      listAuditLogWarm({
        action: [WHITELIST_ACTION],
        // The API wants full ISO timestamps; a date input gives a calendar
        // day, so widen it to cover that whole day in the viewer's zone.
        ...(from ? { date_from: new Date(`${from}T00:00:00`).toISOString() } : {}),
        ...(to ? { date_to: new Date(`${to}T23:59:59.999`).toISOString() } : {}),
        page,
        limit: PAGE_SIZE,
      }),
    staleTime: 30_000,
  });

  const rows = query.data?.rows ?? [];
  // An empty result can report page_count 0 — never let `page` fall to 0.
  const pageCount = Math.max(1, query.data?.meta.page_count ?? 1);

  // Removing the last row of the last page strands the operator.
  //
  // The server answers for a page that no longer exists, so `rows` is empty —
  // and the empty-state branch below replaces the table AND the pager with
  // it, leaving no control to get back to a page that has data. Clamping here
  // turns that into a normal re-fetch of the last real page.
  //
  // Guarded on a settled query: while one is in flight `pageCount` sits at its
  // default of 1, and clamping against that would yank the operator back to
  // page 1 on every page change.
  // Not flagged in review — found by sweeping for the shape the merchants
  // tab was flagged for. Reachable here by narrowing the date range while on
  // a later page, which is a thing this screen invites.
  useEffect(() => {
    if (!query.isPending && !query.isError && page > pageCount) {
      setPage(pageCount);
    }
  }, [query.isPending, query.isError, page, pageCount]);

  // NOTE — there is deliberately no "change kind" filter.
  //
  // Added / Removed / Limits changed is derived from the row's DIFF, which
  // the audit API cannot filter on, so the control could only ever narrow
  // the page already in hand. On an audit screen that is worse than having
  // no filter at all: selecting "Removed" would show the removals on page 1
  // and silently omit the ones on page 2, and an empty table would read as
  // "this merchant was never removed" rather than "look on another page".
  //
  // Restore it only alongside a server-side filter — either an API that can
  // query the diff, or distinct action verbs per change kind at write time.
  //
  // Date range and the action filter ARE server-side, so the row count and
  // page count stay honest.

  const resetPage = (): void => setPage(1);

  function renderLimit(row: AuditV2Row, path: string): JSX.Element {
    const v = limitAfter(row, path);
    if (v === undefined) return <span className="text-pw-text-muted">—</span>;
    if (v === null) {
      return (
        <span className="text-pw-text-muted">{t('whitelist.log.noLimit')}</span>
      );
    }
    return <Money usdCents={v} align="right" />;
  }

  return (
    <div className="space-y-4" data-testid="whitelist-log-tab">
      <div>
        <h2 className="text-sm font-semibold text-pw-text">
          {t('whitelist.log.heading')}
        </h2>
        <p className="mt-1 text-xs text-pw-text-muted">{t('whitelist.log.hint')}</p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs text-pw-text">
          <span className="text-pw-text-muted">{t('whitelist.log.filter.from')}</span>
          <input
            type="date"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              resetPage();
            }}
            data-testid="whitelist-log-from"
            className="h-10 rounded border border-pw-border bg-pw-bg px-3 text-sm text-pw-text outline-none focus-visible:border-pw-primary"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-pw-text">
          <span className="text-pw-text-muted">{t('whitelist.log.filter.to')}</span>
          <input
            type="date"
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
              resetPage();
            }}
            data-testid="whitelist-log-to"
            className="h-10 rounded border border-pw-border bg-pw-bg px-3 text-sm text-pw-text outline-none focus-visible:border-pw-primary"
          />
        </label>
      </div>

      {query.isPending ? (
        <div
          role="status"
          aria-busy="true"
          className="flex min-h-[20vh] items-center justify-center"
          data-testid="whitelist-log-loading"
        >
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-pw-border border-t-pw-primary" />
        </div>
      ) : query.isError ? (
        <div
          className="rounded border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 dark:bg-red-950/40 dark:text-red-300"
          data-testid="whitelist-log-error"
        >
          {query.error?.userMessage ?? t('whitelist.log.loadError')}
        </div>
      ) : rows.length === 0 ? (
        <div
          className="rounded border border-pw-border bg-pw-surface px-4 py-10 text-center text-sm text-pw-text-muted"
          data-testid="whitelist-log-empty"
        >
          {t('whitelist.log.empty')}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded border border-pw-border">
            <table className="w-full text-sm" data-testid="whitelist-log-table">
              <thead className="bg-pw-surface text-left text-xs text-pw-text-muted">
                <tr>
                  <th className="px-3 py-2">{t('whitelist.log.colTime')}</th>
                  <th className="px-3 py-2">{t('whitelist.log.colAdmin')}</th>
                  <th className="px-3 py-2">{t('whitelist.log.colMerchant')}</th>
                  <th className="px-3 py-2">{t('whitelist.log.colChange')}</th>
                  <th className="px-3 py-2 text-right">
                    {t('whitelist.merchants.colPerOrder')}
                  </th>
                  <th className="px-3 py-2 text-right">
                    {t('whitelist.merchants.colPerDay')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className="border-t border-pw-border"
                    data-testid={`whitelist-log-row-${r.id}`}
                  >
                    <td className="px-3 py-2 text-pw-text-muted">
                      {new Date(r.occurred_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-2">
                      {/* Blank for rows written before the acting admin's
                          email was recorded — see `resolveActorEmail`. */}
                      {r.actor_email ?? t('whitelist.log.unknownAdmin')}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-pw-text-muted">
                      {r.target_id}
                    </td>
                    <td className="px-3 py-2">
                      {t(`whitelist.log.change.${classify(r)}`)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {renderLimit(r, 'max_per_order_cents')}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {renderLimit(r, 'max_per_day_cents')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-xs text-pw-text-muted">
            {t('whitelist.log.legacyNote')}
          </p>

          {pageCount > 1 ? (
            <div className="flex items-center gap-3 text-sm">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded border border-pw-border px-3 py-1.5 disabled:opacity-50"
                data-testid="whitelist-log-prev"
              >
                ‹
              </button>
              <span className="text-pw-text-muted">
                {page} / {pageCount}
              </span>
              <button
                type="button"
                disabled={page >= pageCount}
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                className="rounded border border-pw-border px-3 py-1.5 disabled:opacity-50"
                data-testid="whitelist-log-next"
              >
                ›
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

export default WhitelistLogTab;
